export const noop = (): void => { /* noop */ };

/**
 * Creates a handler for nullable data to throw an error if the data is in fact
 * null. This can be used for api request callbacks.
 *
 * @example
 * Pass api result if not null, throw an error otherwise
 * ```ts
 * makeRequest<MyData>('/api/example')
 *   .then(failOnNull('example data was null'))
 * ```
 * @param message The message to fail with
 */
export const failOnNull = <T>(message: string): ((data: T | null) => T) => {
    const err = new Error(message);
    return (data: T | null): T => {
        if (data === null) {
            throw err;
        }
        return data;
    };
};

/**
 * Creates a handler for nullable data to return default data if the data is in
 * fact null. This can be used for api request callbacks.
 *
 * @example
 * Pass api result if not null, return a fallback otherwise
 * ```ts
 * makeRequest<MyData[]>('/api/example')
 *   .then(fallbackOnNull([]))
 * ```
 * @param fallback The value to return in case the passed data is null
 */
export const fallbackOnNull = <T, F extends T = T>(fallback: F): ((data: T | null) => T) => {
    return (data: T | null): T => {
        if (data === null) {
            return fallback;
        }
        return data;
    };
};

/**
 * Returns a function triggering the given callback and returning the original
 * argument.
 * @example
 * Trigger a side effect from a thenable
 * ```ts
 * api.updateRecord<R>(data)
 *  .then(execute(api.reloadList))
 * ```
 */
export const execute = <T>(f: () => void) => (data: T): T => {
    f();
    return data;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type Procedure = (...args: any[]) => any;


/**
 * Debounces a function call
 * @template T
 * @param {T} f
 * @param {number} t
 * @returns {(...args: Parameters<T>) => void}
 */
export const debounce = <T extends Procedure>(f: T, t: number): (...args: Parameters<T>) => void => {
    let handle: number;

    return function executedFunction(...args) {
        const debounced = () => {
            clearTimeout(handle);
            f(...args);
        };

        clearTimeout(handle);
        handle = window.setTimeout(debounced, t);
    };
};

/**
 * Inverts a boolean function, returning the opposite result
 * @param fn The function to invert
 * @returns An inverted version of the function
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const inverse = <T extends (...args: any) => any>(fn: (...args: Parameters<T>) => boolean): ((...args: Parameters<T>) => boolean) =>
    (...args: Parameters<T>) => !fn(...args);

/**
 * Creates a mapper for the given function
 * @template T
 * @template U
 * @param {(item: T, index: number, array: T[]) => U} f
 * @returns {(xs: T[]) => U[]}
 */
export const map = f =>
    xs => xs.map(f);

export const sum = (xs: number[]) =>
    xs.reduce((c, x) => c + x, 0);

/**
 * Creates a function executing all given callbacks with the given arguments
 */
export const chain = (...callbacks: any[]): (...args: any[]) => void => {
    return (...args: any[]) => {
        for (const callback of callbacks) {
            if (typeof callback === 'function') {
                callback(...args);
            }
        }
    };
};
